import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

const ProjectsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 2rem;
  background-color: ${({ theme }) => theme.background};
  background-image: radial-gradient(circle, rgba(255,255,255,0.07) 10%, transparent 10%);
  background-size: 20px 20px;
  animation: backgroundMove 25s linear infinite;

  @keyframes backgroundMove {
    0% { background-position: 0 0; }
    100% { background-position: 100px 100px; }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Header = styled(motion.h2)`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.accent};
  margin-bottom: 2.5rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 60%;
    height: 4px;
    background: linear-gradient(90deg, ${({ theme }) => theme.accent}, #00f0ff);
    bottom: -10px;
    left: 20%;
    animation: underline 1.5s infinite alternate;
  }

  @keyframes underline {
    from { width: 0; }
    to { width: 60%; }
  }
`;

const ProjectsContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2.5rem;
  width: 100%;
  max-width: 1200px;
`;

const ProjectCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.08));
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;

  &:hover {
    transform: translateY(-10px) scale(1.03);
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.25);
  }

  &::before {
    content: '';
    position: absolute;
    top: -30%;
    right: -30%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, ${({ theme }) => theme.accent}33, transparent 60%);
    animation: pulse 6s ease-in-out infinite;
    pointer-events: none;
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.2; }
    50% { transform: scale(1.1); opacity: 0.4; }
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

const ProjectIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.accent};
  text-shadow: 0 0 12px ${({ theme }) => theme.accent};

  ${ProjectCard}:hover & {
    color: white;
    text-shadow: 0 0 20px white;
  }
`;

const ProjectTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.accent};

  ${ProjectCard}:hover & {
    color: #fff;
    transform: scale(1.05);
  }
`;

const ProjectDescription = styled.p`
  color: ${({ theme }) => theme.color};
  opacity: 0.85;
  font-size: 1rem;
  margin-bottom: 1.5rem;

  ${ProjectCard}:hover & {
    color: #f0f0f0;
  }
`;

const ProjectLink = styled.a`
  display: inline-block;
  padding: 0.6rem 1.2rem;
  font-size: 1rem;
  border: 2px solid ${({ theme }) => theme.accent};
  border-radius: 30px;
  color: ${({ theme }) => theme.accent};
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.accent};
    color: ${({ theme }) => theme.background};
    transform: translateY(-2px);
  }
`;

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

const Projects = () => {
  const projects = [
    {
    icon: '🌐',
    title: 'Portfolio Website',
    description: 'My personal portfolio showcasing projects, skills, and contact details in a modern animated design.',
    link: 'https://superb-souffle-5320f1.netlify.app/',
  },
    {
      icon: '💬',
      title: 'Chat Application',
      description: 'Real-time chat app using React and Firebase. Enables live messaging and smooth UX.',
      link: 'https://react-firebase-chat-amber.vercel.app/',
    },
    {
      icon: '📘',
      title: 'Pennote - EdTech Site',
      description: 'Elegant note-sharing platform with responsive design and intuitive UI.',
      link: 'https://harsh-d-lab.github.io/PenNote/',
    },
    {
      icon: '🧠',
      title: 'Simon Game',
      description: 'Classic memory game built with vanilla JS and dynamic difficulty levels.',
      link: 'https://simon-says-game-harshdhama.netlify.app/',
    },
    {
      icon: '📚',
      title: 'Library API',
      description: 'Node.js-based REST API for library management with full CRUD support.',
      link: 'https://github.com/Harsh-d-lab/library-api',
    },
  ];

  return (
    <ProjectsSection id="projects">
      <Header
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        Projects
      </Header>
      <ProjectsContainer
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {projects.map((project, index) => (
          <ProjectCard key={index} variants={cardVariants}>
            <ProjectIcon>{project.icon}</ProjectIcon>
            <ProjectTitle>{project.title}</ProjectTitle>
            <ProjectDescription>{project.description}</ProjectDescription>
            <ProjectLink href={project.link} target="_blank" rel="noopener noreferrer">
              View Project
            </ProjectLink>
          </ProjectCard>
        ))}
      </ProjectsContainer>
    </ProjectsSection>
  );
};

export default Projects;
